import type { Pergunta, TipoPergunta } from '../types';

export const ROTULO_TIPO: Record<TipoPergunta, string> = {
  nota: 'Nota de 0 a 10',
  escala: 'Escala de 1 a 5',
  sim_nao: 'Sim ou não',
  escolha: 'Escolha uma opção',
  texto: 'Texto livre',
};

/** Próximo id interno livre (p1, p2…). O entrevistador nunca vê o id; ele só identifica a pergunta nas respostas. */
export function proximoId(perguntas: Pergunta[]): string {
  const usados = new Set(perguntas.map((p) => p.id));
  for (let n = 1; ; n++) if (!usados.has(`p${n}`)) return `p${n}`;
}

type Props = { perguntas: Pergunta[]; onMudar: (p: Pergunta[]) => void; somenteLeitura: boolean; perguntaAncora: string };

export function ConstrutorPerguntas({ perguntas, onMudar, somenteLeitura, perguntaAncora }: Props) {
  const mudar = (i: number, parcial: Partial<Pergunta>) => onMudar(perguntas.map((p, k) => (k === i ? { ...p, ...parcial } : p)));
  const mover = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= perguntas.length) return;
    const c = [...perguntas];
    [c[i], c[j]] = [c[j]!, c[i]!];
    onMudar(c);
  };

  return (
    <div class="construtor">
      <div class="pergunta ancora">
        <small>Pergunta fixa (sempre a primeira)</small>
        <p><strong>{perguntaAncora}</strong></p>
        <small>Nota de 0 a 10. É dela que saem a aprovação, a reprovação e a média.</small>
      </div>

      {perguntas.length === 0 && <p class="vazio">Nenhuma pergunta adicional. Só a pergunta fixa será feita.</p>}

      {perguntas.map((p, i) => (
        <fieldset key={p.id} class="pergunta" disabled={somenteLeitura}>
          <legend>Pergunta {i + 1}</legend>
          <label class="campo-form">
            <span>Texto da pergunta</span>
            <input value={p.texto} maxLength={200} onInput={(e) => mudar(i, { texto: e.currentTarget.value })} />
          </label>
          <div class="linha-form">
            <label class="campo-form meio">
              <span>Tipo de resposta</span>
              <select
                value={p.tipo}
                onChange={(e) => {
                  const tipo = e.currentTarget.value as TipoPergunta;
                  mudar(i, { tipo, opcoes: tipo === 'escolha' ? (p.opcoes?.length ? p.opcoes : ['', '']) : undefined });
                }}
              >
                {(Object.keys(ROTULO_TIPO) as TipoPergunta[]).map((t) => <option key={t} value={t}>{ROTULO_TIPO[t]}</option>)}
              </select>
            </label>
            <label class="check"><input type="checkbox" checked={p.obrigatoria} onChange={(e) => mudar(i, { obrigatoria: e.currentTarget.checked })} /> Obrigatória</label>
          </div>
          {p.tipo === 'escolha' && (
            <label class="campo-form">
              <span>Opções (uma por linha, de 2 a 10)</span>
              <textarea rows={4} value={(p.opcoes ?? []).join('\n')} onInput={(e) => mudar(i, { opcoes: e.currentTarget.value.split('\n') })} />
            </label>
          )}
          {p.tipo === 'texto' && <small class="aviso">Atenção: peça aos pesquisadores que NUNCA anotem nome, telefone ou CPF no texto livre.</small>}
          {!somenteLeitura && (
            <div class="pergunta-acoes">
              <button type="button" class="link" onClick={() => mover(i, -1)} disabled={i === 0} aria-label={`Subir pergunta ${i + 1}`}>↑ Subir</button>
              <button type="button" class="link" onClick={() => mover(i, 1)} disabled={i === perguntas.length - 1} aria-label={`Descer pergunta ${i + 1}`}>↓ Descer</button>
              <button type="button" class="link perigo-txt" onClick={() => onMudar(perguntas.filter((_, k) => k !== i))}>Remover</button>
            </div>
          )}
        </fieldset>
      ))}

      {!somenteLeitura && perguntas.length < 20 && (
        <button type="button" class="botao" onClick={() => onMudar([...perguntas, { id: proximoId(perguntas), tipo: 'escala', texto: '', obrigatoria: true }])}>
          + Adicionar pergunta
        </button>
      )}
    </div>
  );
}

/** Limpa o rascunho antes de enviar: tira linhas vazias das opções e espaços das pontas. */
export function normalizarPerguntas(ps: Pergunta[]): Pergunta[] {
  return ps.map((p) => ({
    id: p.id,
    tipo: p.tipo,
    texto: p.texto.trim(),
    obrigatoria: p.obrigatoria,
    ...(p.tipo === 'escolha' ? { opcoes: (p.opcoes ?? []).map((o) => o.trim()).filter(Boolean) } : {}),
  }));
}
